import { useState } from "react";
import type { Review } from "../types";
import IconStarFill from "@/components/icons/icon-star-fill";
import IconStarLine from "@/components/icons/icon-star-line";
import AudioPlayer from "@/components/ui/audio-player";
import VideoModal from "./video-modal";

function StarRating({ rating }: { rating: number }) {
	if (!rating || rating === 0) return null;
	return (
		<div className="flex items-center gap-0.5">
			{[1, 2, 3, 4, 5].map((star) => (star <= rating ? <IconStarFill key={star} className="w-4 h-4 text-brand" /> : <IconStarLine key={star} className="w-4 h-4 text-gray-200" />))}
		</div>
	);
}

function ContentTypeBadge({ contentType }: { contentType: string }) {
	const config: Record<string, { label: string; className: string }> = {
		text: { label: "Text", className: "bg-blue-50 text-blue-700" },
		video: { label: "Video", className: "bg-purple-50 text-purple-700" },
		audio: { label: "Audio", className: "bg-orange-50 text-orange-700" },
	};
	const { label, className } = config[contentType] || { label: contentType, className: "bg-gray-100 text-gray-600" };
	return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${className}`}>{label}</span>;
}

function Placeholder({ value, fallback = "—" }: { value: string | null | undefined; fallback?: string }) {
	if (!value || value.trim() === "") {
		return <span className="text-gray-300">{fallback}</span>;
	}
	return <span>{value}</span>;
}

function formatDuration(seconds: number): string {
	const mins = Math.floor(seconds / 60);
	const secs = Math.floor(seconds % 60);
	return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export interface ReviewDraftListProps {
	reviews: Review[];
	onClick: (id: string) => void;
	isLoading?: boolean;
}

function DraftReviewItem({ review, onClick }: { review: Review; onClick: (id: string) => void }) {
	const [videoModalOpen, setVideoModalOpen] = useState(false);
	const isAudio = review.content_type === "audio";
	const isVideo = review.content_type === "video";
	const hasVideo = isVideo && !!review.mux_playback_id;

	return (
		<>
			<div
				onClick={(e) => {
					if ((e.target as HTMLElement).closest("button, audio, a")) return;
					onClick(review.id);
				}}
				className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow cursor-pointer"
			>
				<div className="flex items-start justify-between gap-4">
					<div className="flex-1 min-w-0">
						<div className="flex items-center gap-3 mb-2">
							{review.customer_avatar_url ? (
								<img src={review.customer_avatar_url} alt={review.customer_name || ""} className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
							) : (
								<div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 font-medium text-sm flex-shrink-0">
									{review.customer_name?.charAt(0)?.toUpperCase() || "?"}
								</div>
							)}
							<div className="min-w-0">
								<div className="flex items-center gap-2 flex-wrap">
									<span className="text-sm font-medium text-primary">
										<Placeholder value={review.customer_name} />
									</span>
									<ContentTypeBadge contentType={review.content_type} />
									{review.rating > 0 && <StarRating rating={review.rating} />}
								</div>
								<div className="flex items-center gap-2 mt-0.5">
									{review.customer_email && <span className="text-xs text-tertiary truncate">{review.customer_email}</span>}
								</div>
							</div>
						</div>

						<div className="ml-11">
							{/* Text content */}
							{review.content_text && !isAudio && !isVideo && <p className="text-sm text-secondary line-clamp-2">{review.content_text.length > 150 ? `${review.content_text.slice(0, 150)}...` : review.content_text}</p>}
							{!review.content_text && !isAudio && !isVideo && <p className="text-sm text-gray-300 italic">No content provided</p>}

							{/* Audio Player */}
							{isAudio && (
								<div className="w-64 mt-2">
									{review.audio_url ? (
										<AudioPlayer src={review.audio_url} initialDuration={review.audio_duration} stopPropagation />
									) : (
										<div className="w-full h-24 rounded-lg bg-gray-100 flex items-center justify-center text-xs text-tertiary">No audio available</div>
									)}
								</div>
							)}

							{/* Video Thumbnail */}
							{hasVideo && (
								<button
									type="button"
									onClick={(e) => {
										e.stopPropagation();
										setVideoModalOpen(true);
									}}
									className="relative w-40 h-24 flex-shrink-0 rounded-lg bg-gray-100 overflow-hidden cursor-pointer mt-2"
								>
									{review.video_thumbnail_url ? (
										<img src={review.video_thumbnail_url} alt="Video thumbnail" className="w-full h-full object-cover" />
									) : (
										<div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
											<svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
											</svg>
										</div>
									)}
									<div className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors">
										<div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
											<svg className="w-4 h-4 text-gray-900 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
												<path d="M8 5v14l11-7z" />
											</svg>
										</div>
									</div>
									{review.video_duration && (
										<div className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded bg-black/70 text-white text-xs font-medium">
											{formatDuration(review.video_duration)}
										</div>
									)}
								</button>
							)}

							<div className="flex items-center gap-3 mt-2 flex-wrap">
								<span className="text-xs text-tertiary">
									<Placeholder value={review.created_at ? new Date(review.created_at).toLocaleDateString() : null} />
								</span>
								{review.form?.title && (
									<a
										href={`/${review.workspace_id}/forms/${review.form.id}/edit`}
										onClick={(e) => e.stopPropagation()}
										className="text-xs text-brand hover:underline flex items-center gap-1"
										target="_blank"
										rel="noopener noreferrer"
									>
										{review.form.title}
										<svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
										</svg>
									</a>
								)}
							</div>
						</div>
					</div>

					<div className="flex-shrink-0">
						<svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
						</svg>
					</div>
				</div>
			</div>

			{/* Video Modal */}
			{hasVideo && review.mux_playback_id && <VideoModal open={videoModalOpen} onOpenChange={setVideoModalOpen} playbackId={review.mux_playback_id} />}
		</>
	);
}

export default function ReviewDraftList({ reviews, onClick, isLoading }: ReviewDraftListProps) {
	if (isLoading) {
		return (
			<div className="space-y-3">
				{Array.from({ length: 5 }).map((_, i) => (
					<div key={i} className="bg-white border border-gray-200 rounded-lg p-4 animate-pulse">
						<div className="flex items-center justify-between">
							<div className="flex items-center gap-3">
								<div className="w-8 h-8 rounded-full bg-gray-200" />
								<div className="space-y-2">
									<div className="h-4 bg-gray-200 rounded w-32" />
									<div className="h-3 bg-gray-200 rounded w-24" />
								</div>
							</div>
							<div className="h-4 bg-gray-200 rounded w-20" />
						</div>
						<div className="mt-3 space-y-2">
							<div className="h-3 bg-gray-200 rounded w-full" />
							<div className="h-3 bg-gray-200 rounded w-3/4" />
						</div>
					</div>
				))}
			</div>
		);
	}

	if (reviews.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center py-16 px-4">
				<div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
					<svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
					</svg>
				</div>
				<h3 className="text-base font-semibold text-primary mb-1">No incomplete submissions</h3>
				<p className="text-sm text-tertiary text-center max-w-sm">There are no incomplete submissions at the moment. All form submissions have been completed.</p>
			</div>
		);
	}

	return (
		<div className="space-y-3">
			{reviews.map((review) => (
				<DraftReviewItem key={review.id} review={review} onClick={onClick} />
			))}
		</div>
	);
}